/**
 * should be used for fetching data from storyblok
 * using internally in components
 */
import { ISbStoriesParams } from '@storyblok/react/rsc';
import { errorWrapper } from 'commerce/utils/network';
import { getStoriesUrl, sbParams } from './config';
import { storyblokApi } from './instance';
import { VERSION } from './types';

const getStoryBySlug = async ({
  slug,
  language,
  isEditMode,
}: {
  slug: string;
  language: string;
  isEditMode?: boolean;
}) => {
  const params = { ...sbParams };

  params.version = isEditMode ? VERSION.DRAFT : params.version;

  return errorWrapper(getStoryBySlug.name, () =>
    storyblokApi?.get(getStoriesUrl(slug), {
      ...params,
      language,
      cv: Math.random(),
    }),
  );
};

export const getStoryByIds = async ({
  uuids,
  language,
  isEditMode,
}: {
  uuids: string[];
  language: string;
  isEditMode?: boolean;
}) => {
  const params = { ...sbParams };

  params.version = isEditMode ? VERSION.DRAFT : params.version;

  return !uuids?.length
    ? null
    : errorWrapper(getStoryByIds.name, () =>
        storyblokApi?.getStories({
          ...params,
          language,
          by_uuids_ordered: uuids.join(','),
          cv: Math.random(),
        }),
      );
};

export const getBannersUpsellingStories = (language: string) =>
  getStoryBySlug({
    slug: 'configuration/upselling-banners',
    language,
  });

export const getBrandStories = (
  language: string,
  params?: {
    page?: number;
    name?: string;
    version?: ISbStoriesParams['version'];
    // coma separated
    excluding_fields?: string;
  },
) => {
  return errorWrapper(getBrandStories.name, () => {
    const { page = 1, name, version, excluding_fields } = params || {};
    const config: ISbStoriesParams = {
      ...sbParams,
      language,
      filter_query: { pageType: { like: 'brand' } },
      per_page: 100,
      page,
      version,
    };

    if (name) {
      config.search_term = name;
    }
    if (version) {
      config.version = version;
    }

    if (excluding_fields) {
      config.excluding_fields = excluding_fields;
    }

    return storyblokApi?.getStories(config);
  });
};

export const getStoriesByPrefix = (language: string, slug: string) => {
  return errorWrapper(getStoriesByPrefix.name, () =>
    storyblokApi?.getStories({
      // not possible to get search params in layout.ts
      ...sbParams,
      by_slugs: `${slug}/*`,
      language,
    }),
  );
};
export const getCategoriesForSearchStories = (language: string) => {
  return errorWrapper(getCategoriesForSearchStories.name, () =>
    storyblokApi?.getStories({
      language,
      filter_query: {
        showOnSearchPage: {
          in: true,
        },
      },
    }),
  );
};

export const getBannersTrustStories = (language: string) =>
  getStoryBySlug({
    slug: 'configuration/trust',
    language,
  });

const getStoriesBySlugs = async (
  slugs: string[],
  language: string,
  params?: ISbStoriesParams,
) => {
  return errorWrapper(getStoriesBySlugs.name, () =>
    storyblokApi?.getStories({
      // not possible to get search params in layout.ts
      ...sbParams,
      by_slugs: slugs.join(','),
      language,
      ...params,
    }),
  );
};

export const getSiteConfigStory = async (language: string) =>
  getStoryBySlug({ slug: 'configuration/site', language });

export const getConfigStories = (language: string) =>
  getStoriesBySlugs(
    [
      'configuration/header',
      'configuration/footer',
      'configuration/colors',
      'configuration/shipping',
      'configuration/materialInfo',
      'configuration/productdetail',
      'configuration/deliveryinfo',
    ],
    language,
    { resolve_relations: 'NavigationItem.bannersSet' },
  );
